import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAppLocale } from '../providers/LocaleProvider';
import type { AppLocale } from '../i18n';

const DEFAULT_CONTENT_NAMESPACES = ['learning', 'common', 'notifications'] as const;

export type ContentLocaleAttrs = {
  dir: 'ltr' | 'rtl';
  lang: AppLocale;
};

/**
 * Translation + layout for learner content containers (lesson body, quiz/exercise modal body).
 * When `forceEnglish` is true, resolves UI strings from the `en` bundle while page chrome stays on the user locale.
 */
export function useContentLocaleScope(
  forceEnglish: boolean,
  namespaces: string | readonly string[] = DEFAULT_CONTENT_NAMESPACES
): {
  tContent: TFunction;
  contentLocaleAttrs: ContentLocaleAttrs;
} {
  const { locale, direction } = useAppLocale();
  const ns = namespaces as string | string[];
  const { t: uiT } = useTranslation(ns);
  const { t: enT } = useTranslation(ns, { lng: 'en' });

  return useMemo(() => {
    if (forceEnglish) {
      return {
        tContent: enT,
        contentLocaleAttrs: { dir: 'ltr', lang: 'en' },
      };
    }
    return {
      tContent: uiT,
      contentLocaleAttrs: { dir: direction, lang: locale },
    };
  }, [forceEnglish, enT, uiT, direction, locale]);
}
